import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Checkbox,
  CircularProgress,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'date-fns';
import { saveAs } from 'file-saver';

import { getAssignmentPricings, getRegions } from '../../repositories/HttpRepository';
import { StyledTableCell, StyledTableRow, useStyles } from './styles';
import moment from 'moment';

function secondsToHms(d) {
  d = Number(d);
  let h = Math.floor(d / 3600);
  let m = Math.floor((d % 3600) / 60);
  let s = Math.floor((d % 3600) % 60);

  let hDisplay = h > 0 ? h + ':' : '';
  let mDisplay = m > 0 ? m + ':' : '00:';
  let sDisplay = s && s !== '' ? s : '00';
  // console.log('d is: ', d);
  // console.log('s is: ', s, h);

  return hDisplay + mDisplay + sDisplay;
}

export default function DriverPricing() {
  const { classes } = useStyles();
  const [message, setMessage] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [selectRegions, setSelectRegions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [baseHourRate, setBaseHourRate] = useState(15.5);
  const [premiumRate, setPremiumRate] = useState(0.5);
  const [pickupTime, setPickupTime] = useState(30);
  const [assignmentIds, setAssignmentIds] = useState('');
  const [maxDiffDistance, setMaxDiffDistance] = useState(10000);
  const [maxDiffNoPickup, setMaxDiffNoPickup] = useState(5);
  const [loading, setLoading] = useState(false);

  // filters
  const sd = new Date();
  sd.setDate(sd.getDate() - 1);
  const [startDate, setStartDate] = useState(moment(sd));
  const [endDate, setEndDate] = useState(moment());

  const handleDateChange = (setValue) => (date) => {
    setValue(date);
  };

  useEffect(() => {
    getRegions().then((resp) => {
      if (resp.ok) {
        setSelectRegions(resp.data);
      }
    });
  }, []);

  const downloadCsv = (e) => {
    let data = ['Assignment ID, Driver ID, Shipment Count, Pickup Count, Target Delivery Time, Engaged Delivery Time, Working Time, Current Assignment Price, Calculated Assignment Price'];

    data = data.concat(
      assignments.map((assignment) => {
        const cols = [
          assignment.origin_assignment.id,
          assignment.origin_assignment.driver_id,
          assignment.shipment_count,
          assignment.pickup_count,
          assignment.target_driving_time,
          (assignment.driving_time / 1000).toFixed(2),
          (assignment.driving_time / 1000 + pickupTime).toFixed(2),
          `$${assignment.origin_assignment.tour_cost}`,
          `$${assignment.calculated_price.toFixed(2)}`,
        ];
        return cols.join(',') + ' \n';
      }),
    );

    // console.log('data is: ', data);

    let file = new File(data, `data.csv`, { type: 'text/plain;charset=utf-8' });
    saveAs(file);
  };

  const getAssignments = () => {
    setLoading(true);
    const payload = {
      start: startDate,
      end: endDate,
      regions,
      base_hour_rate: baseHourRate,
      premium_rate: premiumRate,
      pickup_time: pickupTime,
      assignment_ids: assignmentIds,
    };
    getAssignmentPricings(payload).then((resp) => {
      // console.log('resp', resp);
      setLoading(false);
      if (resp.ok) {
        setAssignments(resp.data);
      }
    });
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 3 }}>
        <form>
          <Box sx={{ pb: 3 }}>
            <Grid container spacing={2}>
              <Grid size={3}>
                <TextField id="assignment-ids" label="Assignment Ids" type="text" value={assignmentIds} onChange={(e) => setAssignmentIds(e.target.value)} />
              </Grid>
              <Grid size={3}>
                <TextField id="max-diff-distance" label="Max Different Distance (m)" type="number" value={maxDiffDistance} onChange={(e) => setMaxDiffDistance(e.target.value)} />
              </Grid>
              <Grid size={3}>
                <TextField id="max-diff-no-pickup" label="Max Different Number Pickup" type="number" value={maxDiffNoPickup} onChange={(e) => setMaxDiffNoPickup(e.target.value)} />
              </Grid>
              <Grid size={3}></Grid>

              <Grid size={3}>
                <TextField id="base-price" label="Base" type="number" value={baseHourRate} onChange={(e) => setBaseHourRate(e.target.value)} />
              </Grid>
              <Grid size={3}>
                <TextField id="premium-price" label="Premium" type="number" value={premiumRate} onChange={(e) => setPremiumRate(e.target.value)} />
              </Grid>
              <Grid size={3}>
                <TextField id="pickup-time" label="Pickup Time - Seconds" type="number" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} />
              </Grid>
              <Grid size={3}></Grid>

              <Grid size={3}>
                <DateTimePicker
                  disableToolbar
                  variant="inline"
                  format="MM/DD/YYYY"
                  margin="normal"
                  id="start-date"
                  label="Start Date"
                  value={startDate}
                  onChange={handleDateChange(setStartDate)}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                />
              </Grid>
              <Grid size={3}>
                <DateTimePicker
                  disableToolbar
                  variant="inline"
                  format="MM/DD/YYYY"
                  margin="normal"
                  id="end-date"
                  label="End Date"
                  value={endDate}
                  onChange={handleDateChange(setEndDate)}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                />
              </Grid>
              <Grid size={3}>
                <FormControl className={classes.formControl}>
                  <InputLabel id="region">Regions</InputLabel>
                  <Select
                    labelId="region"
                    id="region"
                    multiple
                    value={regions}
                    onChange={(e) => {
                      setRegions(e.target.value);
                    }}
                    // input={<Input />}
                    variant="outlined"
                    renderValue={(selected) => selected.join(', ')}
                    MenuProps={MenuProps}
                  >
                    {selectRegions.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={regions.indexOf(name) > -1} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={3}>
                <Box>
                  <div className={classes.wrapper}>
                    <Button variant="contained" color="primary" className={classes.buttonSuccess} disabled={loading} onClick={getAssignments}>
                      Load Info
                    </Button>
                    {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
                  </div>
                  {assignments && assignments.length > 0 && (
                    <div className={classes.wrapper}>
                      <Button variant="contained" color="primary" className={classes.buttonSuccess} disabled={loading} onClick={downloadCsv}>
                        Download CSV
                      </Button>
                      {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
                    </div>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </form>
        {message && <Alert severity="success">{message}</Alert>}
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Assignment ID</StyledTableCell>
                <StyledTableCell>Driver ID</StyledTableCell>
                <StyledTableCell>Shipment Count</StyledTableCell>
                <StyledTableCell>Pickup Count</StyledTableCell>
                <StyledTableCell>Target Delivery Time</StyledTableCell>
                <StyledTableCell>Engaged Delivery Time</StyledTableCell>
                <StyledTableCell>Working Time</StyledTableCell>
                <StyledTableCell>Current Assignment Price</StyledTableCell>
                <StyledTableCell>Calculated Price</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments &&
                assignments.length > 0 &&
                assignments.map((assignment) => (
                  <StyledTableRow key={assignment.origin_assignment.id}>
                    <StyledTableCell component="th" scope="row">
                      {assignment.origin_assignment.id}
                    </StyledTableCell>
                    <StyledTableCell>{assignment.origin_assignment.driver_id}</StyledTableCell>
                    <StyledTableCell>{assignment.shipment_count}</StyledTableCell>
                    <StyledTableCell>{assignment.pickup_count}</StyledTableCell>
                    <StyledTableCell>{secondsToHms(assignment.target_driving_time)}</StyledTableCell>
                    <StyledTableCell>{secondsToHms(assignment.driving_time / 1000)}</StyledTableCell>
                    <StyledTableCell>{secondsToHms(assignment.driving_time / 1000 + pickupTime * assignment.pickup_count)}</StyledTableCell>
                    <StyledTableCell>${assignment.origin_assignment.tour_cost}</StyledTableCell>
                    <StyledTableCell>${assignment.calculated_price.toFixed(2)}</StyledTableCell>
                  </StyledTableRow>
                ))}
              {(!assignments || assignments.length <= 0) && (
                <StyledTableRow>
                  <StyledTableCell align="center" colSpan={9}>
                    No Assignments!
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
}
